import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Activity,
  Server,
  Database,
  Layers,
  Wifi,
  CreditCard,
  AlertTriangle,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Info,
  Cpu,
  HardDrive,
  Zap,
  ArrowRight
} from "lucide-react";
import PageHeader from "../../components/PageHeader";
import LoadingScreen from "../../components/LoadingScreen";
import { getMonitoringSummary } from "../../services/adminMonitoring";

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return "—";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(1)} MB`;
};

const formatUptime = (seconds) => {
  if (!seconds && seconds !== 0) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const formatTime = (isoString) => {
  if (!isoString) return null;
  try {
    const d = new Date(isoString);
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return null;
  }
};

const StatusBadge = ({ status }) => {
  const styles = {
    healthy: "bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400",
    warning: "bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400",
    critical: "bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-rose-400",
    unknown: "bg-gray-500/10 border-gray-500/20 text-gray-600 dark:text-gray-400"
  };

  const icons = {
    healthy: <CheckCircle2 size={14} />,
    warning: <AlertTriangle size={14} />,
    critical: <XCircle size={14} />,
    unknown: <Info size={14} />
  };

  const labels = {
    healthy: "Healthy",
    warning: "Warning",
    critical: "Critical",
    unknown: "Unknown"
  };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${styles[status] || styles.unknown}`}>
      {icons[status] || icons.unknown}
      {labels[status] || "Unknown"}
    </span>
  );
};

const MetricRow = ({ label, value, sub }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-xs text-gray-600 dark:text-gray-400">{label}</span>
    <div className="text-right">
      <span className="text-xs font-bold text-gray-900 dark:text-white">{value ?? "—"}</span>
      {sub && <span className="block text-[10px] text-gray-500 dark:text-gray-400">{sub}</span>}
    </div>
  </div>
);

const Monitoring = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getMonitoringSummary();
      setSummary(data);
      setLastUpdated(data.generatedAt || new Date().toISOString());
    } catch (err) {
      setError(err.message || "Erro ao carregar monitoramento.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
    intervalRef.current = setInterval(loadSummary, 60000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [loadSummary]);

  if (loading && !summary) {
    return <LoadingScreen message="Carregando dados de monitoramento..." />;
  }

  const timeStr = formatTime(lastUpdated);

  return (
    <div className="space-y-8">
      <PageHeader
        breadcrumb="Sistema"
        title="Monitoramento da Plataforma"
        subtitle="Visão operacional global da saúde do sistema"
        action={
          <div className="flex items-center gap-3">
            {timeStr && (
              <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500">
                Atualizado em {timeStr}
              </span>
            )}
            <button
              onClick={loadSummary}
              className="flex items-center gap-2 px-4 py-2 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 border border-gray-200 dark:border-white/10 text-xs font-semibold text-gray-700 dark:text-gray-200 rounded-xl transition-all"
            >
              <RefreshCw size={14} /> Atualizar
            </button>
          </div>
        }
      />

      {error && (
        <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-300 text-xs font-semibold flex items-center gap-3">
          <AlertTriangle size={18} /> {error}
        </div>
      )}

      {summary && (
        <>
          {/* BLOCO 1 — STATUS GERAL */}
          <div className="neumorphic-card rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Activity size={22} className="text-purple-600 dark:text-purple-400" />
                <div>
                  <h2 className="text-sm font-bold text-gray-900 dark:text-white">Status Geral da Plataforma</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {summary.overall?.issues || 0} issue(s) detectado(s)
                  </p>
                </div>
              </div>
              <StatusBadge status={summary.overall?.status || "unknown"} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* BLOCO 2 — BACKEND */}
            <div className="neumorphic-card rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                  <Server size={16} className="text-purple-600 dark:text-purple-400" /> Backend
                </h3>
                <StatusBadge status={summary.backend?.status || "unknown"} />
              </div>
              <div className="space-y-0.5">
                <MetricRow label="Uptime" value={formatUptime(summary.backend?.uptimeSeconds)} />
                <MetricRow label="PID" value={summary.backend?.pid} />
                <MetricRow label="Node.js" value={summary.backend?.nodeVersion} />
                <MetricRow label="Plataforma" value={summary.backend?.platform} />
                <MetricRow
                  label="Heap Usage"
                  value={summary.backend?.memory?.heapUsedPercent !== null ? `${summary.backend.memory.heapUsedPercent}%` : "—"}
                  sub={`${formatBytes(summary.backend?.memory?.heapUsedBytes)} / ${formatBytes(summary.backend?.memory?.heapTotalBytes)}`}
                />
                <MetricRow label="RSS" value={formatBytes(summary.backend?.memory?.rssBytes)} />
                <MetricRow label="External" value={formatBytes(summary.backend?.memory?.externalBytes)} />
              </div>
            </div>

            {/* BLOCO 3 — REDIS */}
            <div className="neumorphic-card rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                  <Database size={16} className="text-purple-600 dark:text-purple-400" /> Redis
                </h3>
                <StatusBadge status={summary.redis?.status || "unknown"} />
              </div>
              <div className="space-y-0.5">
                <MetricRow
                  label="Latência"
                  value={summary.redis?.latencyMs !== null ? `${summary.redis.latencyMs}ms` : "—"}
                />
                {summary.redis?.reason && (
                  <MetricRow label="Motivo" value={summary.redis.reason} />
                )}
              </div>
            </div>
          </div>

          {/* BLOCO 4 — FILAS */}
          <div className="neumorphic-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                <Layers size={16} className="text-purple-600 dark:text-purple-400" /> Filas / Jobs
              </h3>
              <StatusBadge status={summary.queues?.status || "unknown"} />
            </div>
            {summary.queues?.queues && summary.queues.queues.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-white/5">
                      <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-semibold">Fila</th>
                      <th className="text-right py-2 px-3 text-gray-500 dark:text-gray-400 font-semibold">Waiting</th>
                      <th className="text-right py-2 px-3 text-gray-500 dark:text-gray-400 font-semibold">Active</th>
                      <th className="text-right py-2 px-3 text-gray-500 dark:text-gray-400 font-semibold">Delayed</th>
                      <th className="text-right py-2 px-3 text-gray-500 dark:text-gray-400 font-semibold">Failed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.queues.queues.map((q) => (
                      <tr key={q.name} className="border-b border-gray-50 dark:border-white/[0.02] hover:bg-black/[0.02] dark:hover:bg-white/[0.02]">
                        <td className="py-2 px-3 font-semibold text-gray-900 dark:text-white">{q.name}</td>
                        <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{q.waiting ?? "—"}</td>
                        <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{q.active ?? "—"}</td>
                        <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{q.delayed ?? "—"}</td>
                        <td className="py-2 px-3 text-right">
                          <span className={`font-semibold ${(q.failed || 0) > 0 ? "text-rose-600 dark:text-rose-400" : "text-gray-700 dark:text-gray-300"}`}>
                            {q.failed ?? "—"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {summary.queues?.totals && (
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-white/5 flex gap-4 text-[11px]">
                    <span className="text-gray-500 dark:text-gray-400">Total Waiting: <span className="font-bold text-gray-900 dark:text-white">{summary.queues.totals.waiting}</span></span>
                    <span className="text-gray-500 dark:text-gray-400">Total Active: <span className="font-bold text-gray-900 dark:text-white">{summary.queues.totals.active}</span></span>
                    <span className="text-gray-500 dark:text-gray-400">Total Failed: <span className={`font-bold ${(summary.queues.totals.failed || 0) > 0 ? "text-rose-600 dark:text-rose-400" : "text-gray-900 dark:text-white"}`}>{summary.queues.totals.failed}</span></span>
                    <span className="text-gray-500 dark:text-gray-400">Total Delayed: <span className="font-bold text-gray-900 dark:text-white">{summary.queues.totals.delayed}</span></span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400">Nenhuma fila disponível.</p>
            )}
          </div>

          {/* BLOCO 5 — WHATSAPP */}
          <div className="neumorphic-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                <Wifi size={16} className="text-purple-600 dark:text-purple-400" /> Conexões WhatsApp
              </h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center p-3 rounded-xl bg-gray-50 dark:bg-white/5">
                <div className="text-2xl font-extrabold text-gray-900 dark:text-white">{summary.connections?.total ?? "—"}</div>
                <div className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-1">Total</div>
              </div>
              <div className="text-center p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
                <div className="text-2xl font-extrabold text-emerald-600 dark:text-emerald-400">{summary.connections?.connected ?? "—"}</div>
                <div className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-1">Conectadas</div>
              </div>
              <div className="text-center p-3 rounded-xl bg-rose-500/5 border border-rose-500/10">
                <div className="text-2xl font-extrabold text-rose-600 dark:text-rose-400">{summary.connections?.disconnected ?? "—"}</div>
                <div className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-1">Desconectadas</div>
              </div>
              <div className="text-center p-3 rounded-xl bg-amber-500/5 border border-amber-500/10">
                <div className="text-2xl font-extrabold text-amber-600 dark:text-amber-400">{summary.connections?.attention ?? "—"}</div>
                <div className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-1">Atenção</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3 text-[11px]">
              <div className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-white/5">
                <span className="text-gray-500 dark:text-gray-400">Connecting</span>
                <span className="font-bold text-gray-700 dark:text-gray-200">{summary.connections?.connecting ?? "—"}</span>
              </div>
              <div className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-white/5">
                <span className="text-gray-500 dark:text-gray-400">QR Required</span>
                <span className="font-bold text-gray-700 dark:text-gray-200">{summary.connections?.qrRequired ?? "—"}</span>
              </div>
              <div className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-white/5">
                <span className="text-gray-500 dark:text-gray-400">Recovering</span>
                <span className="font-bold text-gray-700 dark:text-gray-200">{summary.connections?.recovering ?? "—"}</span>
              </div>
            </div>
          </div>

          {/* BLOCO 6 — BILLING */}
          <div className="neumorphic-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                <CreditCard size={16} className="text-purple-600 dark:text-purple-400" /> Billing & Integrações
              </h3>
              <StatusBadge status={summary.billing?.status || "unknown"} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center p-3 rounded-xl bg-gray-50 dark:bg-white/5">
                <div className={`text-2xl font-extrabold ${summary.billing?.configured ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                  {summary.billing?.configured === null ? "—" : summary.billing.configured ? "Sim" : "Não"}
                </div>
                <div className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-1">Asaas Config</div>
              </div>
              <div className="text-center p-3 rounded-xl bg-gray-50 dark:bg-white/5">
                <div className={`text-2xl font-extrabold ${summary.billing?.webhookEnabled ? "text-emerald-600 dark:text-emerald-400" : "text-gray-400"}`}>
                  {summary.billing?.webhookEnabled ? "Ativo" : "Inativo"}
                </div>
                <div className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-1">Webhook</div>
              </div>
              <div className="text-center p-3 rounded-xl bg-gray-50 dark:bg-white/5">
                <div className={`text-2xl font-extrabold ${summary.billing?.writeEnabled ? "text-emerald-600 dark:text-emerald-400" : "text-gray-400"}`}>
                  {summary.billing?.writeEnabled ? "Habilitada" : "Desabilitada"}
                </div>
                <div className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-1">Escrita</div>
              </div>
              <div className="text-center p-3 rounded-xl bg-gray-50 dark:bg-white/5">
                <div className="text-2xl font-extrabold text-gray-900 dark:text-white">
                  {summary.billing?.failedWebhooksLast24h ?? "—"}
                </div>
                <div className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-1">Webhook Fails (24h)</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3 text-[11px]">
              <div className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-white/5">
                <span className="text-gray-500 dark:text-gray-400">Ambiente</span>
                <span className="font-bold text-gray-700 dark:text-gray-200">{summary.billing?.environment || "—"}</span>
              </div>
              <div className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-white/5">
                <span className="text-gray-500 dark:text-gray-400">Shadow Mode</span>
                <span className="font-bold text-gray-700 dark:text-gray-200">{summary.billing?.shadowMode ? "Sim" : "Não"}</span>
              </div>
              <div className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-white/5">
                <span className="text-gray-500 dark:text-gray-400">Billing Errors (24h)</span>
                <span className="font-bold text-gray-700 dark:text-gray-200">{summary.billing?.billingErrorsLast24h ?? "—"}</span>
              </div>
            </div>
          </div>

          {/* BLOCO 7 — INCIDENTES */}
          <div className="neumorphic-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-900 dark:text-gray-200 flex items-center gap-2">
                <AlertTriangle size={18} className="text-amber-500 dark:text-amber-400" /> Incidentes
              </h3>
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-300">
                {summary.incidents?.length || 0}
              </span>
            </div>
            {summary.incidents && summary.incidents.length > 0 ? (
              <div className="space-y-2 mt-3">
                {summary.incidents.map((incident, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-xl border text-xs flex items-center justify-between ${
                      incident.severity === "critical"
                        ? "bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-rose-300"
                        : incident.severity === "warning"
                        ? "bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-300"
                        : "bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-300"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {incident.severity === "critical" ? <XCircle size={16} /> :
                       incident.severity === "warning" ? <AlertTriangle size={16} /> :
                       <Info size={16} />}
                      <span>
                        <span className="font-semibold">{incident.title}</span>
                        {incident.message && <span className="ml-2 opacity-80">{incident.message}</span>}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {incident.count !== null && (
                        <span className="text-[10px] font-bold">×{incident.count}</span>
                      )}
                      <span className="text-[10px] font-mono opacity-60">{incident.code}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Nenhum incidente registrado. A plataforma está operando normalmente.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Monitoring;
